import { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const DAYS = ['일','월','화','수','목','금','토'];
const MONTHS = ['1월','2월','3월','4월','5월','6월','7월','8월','9월','10월','11월','12월'];

interface Props {
  value: string;
  onChange: (date: string) => void;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  label?: string;
}

function daysInMonth(y: number, m: number) { return new Date(y, m+1, 0).getDate(); }
function firstDay(y: number, m: number) { return new Date(y, m, 1).getDay(); }
function fmt(y: number, m: number, d: number) {
  return `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
}

export default function DatePicker({ value, onChange, minDate, maxDate, placeholder = '날짜 선택', label }: Props) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });

  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  const selected = value ? new Date(value + 'T00:00:00') : null;
  const min = minDate ? new Date(minDate + 'T00:00:00') : null;
  const max = maxDate ? new Date(maxDate + 'T00:00:00') : null;

  // Calculate popup position when opening
  const openPopup = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const top = rect.bottom + 4;
      const left = Math.min(rect.left, window.innerWidth - 310);
      setPopupPos({ top, left });
    }
    setOpen(true);
  };

  // Close on scroll
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, { passive: true, once: true });
    return () => window.removeEventListener('scroll', close);
  }, [open]);

  const days = useMemo(() => {
    const total = daysInMonth(viewYear, viewMonth);
    const start = firstDay(viewYear, viewMonth);
    const nowKey = fmt(today.getFullYear(), today.getMonth(), today.getDate());
    const cells: { d: number; key: string; dim: boolean; today: boolean; disabled: boolean; selected: boolean }[] = [];

    const prevTotal = daysInMonth(viewYear, viewMonth === 0 ? 11 : viewMonth - 1);
    for (let i = start - 1; i >= 0; i--) {
      const d = prevTotal - i;
      cells.push({ d, key: `p-${d}`, dim: true, today: false, disabled: true, selected: false });
    }

    for (let d = 1; d <= total; d++) {
      const key = fmt(viewYear, viewMonth, d);
      const date = new Date(viewYear, viewMonth, d);
      let disabled = false;
      if (min && date < min) disabled = true;
      if (max && date > max) disabled = true;
      cells.push({ d, key, dim: false, today: key === nowKey, disabled, selected: value === key });
    }

    const remaining = 42 - cells.length;
    for (let d = 1; d <= remaining; d++) {
      cells.push({ d, key: `n-${d}`, dim: true, today: false, disabled: true, selected: false });
    }
    return cells;
  }, [viewYear, viewMonth, value, minDate, maxDate]);

  const displayValue = value
    ? `${new Date(value + 'T00:00:00').getMonth()+1}월 ${new Date(value + 'T00:00:00').getDate()}일`
    : '';

  const popup = open && createPortal(
    <>
      <div className="fixed inset-0 z-[9998]" onClick={() => setOpen(false)} />
      <div
        className="fixed z-[9999] bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 w-[300px]"
        style={{ top: popupPos.top, left: popupPos.left }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => {
            if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear-1); }
            else setViewMonth(viewMonth-1);
          }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
          </button>
          <span className="text-sm font-semibold text-slate-800">{viewYear}년 {MONTHS[viewMonth]}</span>
          <button onClick={() => {
            if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear+1); }
            else setViewMonth(viewMonth+1);
          }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 mb-1">
          {DAYS.map((d, i) => (
            <div key={d} className={`text-center text-[11px] font-medium py-1 ${i===0?'text-red-400':i===6?'text-blue-400':'text-gray-400'}`}>{d}</div>
          ))}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7">
          {days.map(cell => (
            <button
              key={cell.key}
              type="button"
              disabled={cell.disabled}
              onClick={() => { onChange(cell.key); setOpen(false); }}
              className={`
                w-full aspect-square flex items-center justify-center text-[13px] rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500
                ${cell.dim ? 'text-gray-200 cursor-default' : ''}
                ${cell.disabled && !cell.dim ? 'text-gray-300 cursor-not-allowed' : ''}
                ${!cell.dim && !cell.disabled && !cell.selected && !cell.today ? 'text-slate-700 hover:bg-gray-100' : ''}
                ${cell.today && !cell.selected ? 'font-bold text-slate-900 bg-gray-50' : ''}
                ${cell.selected ? 'bg-slate-900 text-white font-semibold' : ''}
              `}
            >
              {cell.d}
            </button>
          ))}
        </div>

        {value && (
          <button onClick={() => { onChange(''); setOpen(false); }}
            className="mt-3 w-full text-xs text-gray-400 hover:text-red-500 py-1.5 rounded-lg hover:bg-red-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-brand-500">
            초기화
          </button>
        )}
      </div>
    </>,
    document.body
  );

  return (
    <div>
      {label && <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>}
      <button
        ref={triggerRef}
        type="button"
        onClick={openPopup}
        className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl border text-sm transition-colors duration-150 bg-white focus:outline-none focus:ring-2 focus:ring-brand-500 ${
          open ? 'border-slate-400 ring-2 ring-slate-100' : 'border-gray-200 hover:border-gray-300'
        } ${!value ? 'text-gray-400' : 'text-slate-700'}`}
      >
        <span>{displayValue || placeholder}</span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {popup}
    </div>
  );
}
